import { memo } from 'react';

import { Box, Container, useMediaQuery, useTheme } from '@mui/material';
import dynamic from 'next/dynamic';

import Disclaimer from '@/components/Disclaimer';
import { textToJumpLink } from '@/utils/textToJumpLink/textToJumpLink';

import { IProductFeatureProps } from './ProductFeature.type';
import {
	disclaimerWrapperStyles,
	groupedContentStyles,
	headerSectionStyles,
	mediaSliderStylesSection,
	wrapper,
} from './ProductFeature.styles';
import Heading from './Heading/Heading';
import Description from './Description/Description';
import DescriptionList from './DescriptionList/DescriptionList';
import ButtonCarousel from './ButtonCarousel/ButtonCarousel';
const MediaSlider = dynamic(() => import('./MediaSlider/MediaSlider'));

const ProductFeatureContent = memo(function ProductFeatureContent({
	headline,
	descriptionRichText,
	descriptionButton,
	descriptionButtonsCollection,
	descriptionListCollection,
	buttonsCollection,
	media,
	disclaimer,
	enableSliderControlsBackgroundGradient = true,
}: IProductFeatureProps) {
	const disclaimerData = disclaimer?.disclaimer;
	return (
		<>
			<Box sx={headerSectionStyles}>
				<Heading {...headline} />
			</Box>
			<Box sx={mediaSliderStylesSection}>
				<MediaSlider
					{...media}
					enableSliderControlsBackgroundGradient={enableSliderControlsBackgroundGradient ?? true}
				/>
			</Box>
			<Box sx={groupedContentStyles}>
				<Description
					descriptionRichText={descriptionRichText}
					descriptionButton={descriptionButton}
					descriptionButtonsCollection={descriptionButtonsCollection}
				/>
				<DescriptionList descriptionListCollection={descriptionListCollection} />
				<ButtonCarousel buttonsCollection={buttonsCollection} />
				{disclaimerData ? (
					<Disclaimer
						disclaimer={disclaimerData}
						dataTestId="product_feature_disclaimer"
						wrapperSx={disclaimerWrapperStyles}
						disableGutters
					/>
				) : null}
			</Box>
		</>
	);
});

export default function ProductFeature(props: IProductFeatureProps) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	return (
		<Container
			{...(props?.anchorId && { id: textToJumpLink(props.anchorId) })}
			data-testid={`section_product_feature_${isMobile ? 'mobile' : 'desktop'}`}
			sx={wrapper}
			maxWidth={false}
		>
			<ProductFeatureContent {...props} />
		</Container>
	);
}
